import { toJS } from 'mobx';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useStores } from '../../stores';
import properties from '../properties';
import { ICohortDetails } from '../../_components/organisms/CreateNewModalCoreComponent';
import { roundNumber } from '../helper-utils/helper';
import {
  buildFilterFlowData,
  buildSearchParams,
} from '../helper-utils/cohortModalHelper';

export interface ToggleValue {
  toggleValue?: 'and' | 'or';
}

export interface UseReportsCohortItemProps {
  totalResults: number;
  type?: string;
  existingMemberUniqueIds?: string[];
}
export type CohortChangeType =
  | 'category'
  | 'property'
  | 'condition'
  | 'multipleProperties'
  | 'subCategory';

export const useReportsCohortItem = ({
  totalResults,
  type,
  existingMemberUniqueIds,
}: UseReportsCohortItemProps) => {
  const {
    cohortDetailsStore,
    summaryCardsStore,
    quaterlyReportStore,
    employerGroupStore,
  } = useStores();

  const cohortDetailsList = toJS(cohortDetailsStore.draftCohortDetailsList);
  const toggleList = toJS(cohortDetailsStore.toggleList);

  const categoryDropDownOptions = toJS(
    cohortDetailsStore.categoryDropDownOptions,
  );
  const propertyDropDownOptions = toJS(
    cohortDetailsStore.propertyDropDownOptions,
  );
  const cohortSelectionsTypes = toJS(cohortDetailsStore.cohortSelectionsTypes);

  const eg_nid = employerGroupStore.employerGroupResponseData.eg_nid;
  const max_claim_date =
    employerGroupStore.employerGroupResponseData.max_claim_date;
  const {
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
  } = quaterlyReportStore;

  const startDate =
    aggregationParameter === 'paid' ? paidStartDate : incurredStartDate;
  const endDate =
    aggregationParameter === 'paid' ? paidEndDate : incurredEndDate;

  const [loadingPropertiesByIndex, setLoadingPropertiesByIndex] = useState<
    Record<number, boolean>
  >({});
  const [propertiesSearchValue, setPropertiesSearchValue] = useState('');

  const updateToggleList = useCallback(
    (updated: ToggleValue[]) => {
      cohortDetailsStore.setToggleList(updated);
      summaryCardsStore.addToggleConditions(updated);
    },
    [cohortDetailsStore, summaryCardsStore],
  );

  const addToggleObject = useCallback(() => {
    if (toggleList.length < cohortDetailsList.length - 1) {
      updateToggleList([...toggleList, { toggleValue: 'and' }]);
    }
  }, [toggleList, cohortDetailsList.length, updateToggleList]);

  const handlePageNumber = useCallback(
    (index: number, pageNumber: number, pageType: string) => {
      const updated = cohortDetailsList.map((cohort, i) => {
        if (i !== index) {
          return cohort;
        }
        return { ...cohort, [pageType]: pageNumber };
      });
      cohortDetailsStore.setDraftCohortDetailsList(updated);
    },
    [cohortDetailsList, cohortDetailsStore],
  );

  const getPropertyValues = useCallback(
    async (
      category: string,
      searchValue: string,
      pageNumber: number,
      isSearch: boolean,
      cohortIndex: number,
      dropdownSelectionTypeIndex: number,
    ) => {
      setLoadingPropertiesByIndex((prev) => ({ ...prev, [cohortIndex]: true }));
      try {
        const res = await axios.get(
          properties.aiqBaseUrl +
            properties.summaryBaseUrl +
            properties.drillDown +
            properties.drillDownSearch,
          { params: { category, searchValue, pageNumber } },
        );

        if (res.status === 200) {
          const newData = res.data.data[0].data;
          const updated = propertyDropDownOptions.map((opts, i) => {
            if (i !== dropdownSelectionTypeIndex) {
              return opts;
            }
            return isSearch || pageNumber === 0
              ? [...newData]
              : [...opts, ...newData];
          });

          cohortDetailsStore.setPropertyDropDownOptions(updated);

          handlePageNumber(
            cohortIndex,
            Math.ceil(res.data.data[0].totalSize / 10),
            'totalPagesForProperties',
          );
        }
      } catch (error) {
        return;
      } finally {
        setLoadingPropertiesByIndex((prev) => ({
          ...prev,
          [cohortIndex]: false,
        }));
      }
    },
    [propertyDropDownOptions, handlePageNumber, cohortDetailsStore],
  );

  const getTotalMembers = useCallback(
    async (
      cohorts: ICohortDetails[],
      currentToggleList: ToggleValue[],
    ): Promise<number | null> => {
      const searchParams = buildSearchParams(cohorts, currentToggleList);
      cohortDetailsStore.setIsLoadingCohort(true);
      try {
        const response = await axios.post(
          properties.aiqBaseUrl +
            properties.summaryBaseUrl +
            properties.searchCohortMembers,
          {
            startDate,
            endDate,
            eg_nid,
            searchParams,
            type: type ?? 'data',
            member_unique_id_list: existingMemberUniqueIds ?? [],
            max_claim_date,
          },
        );
        const data = response.data.data;
        cohortDetailsStore.addDraftMemberUniqueIdList(data.member_ids);
        return data.member_ids_count ?? 0;
      } catch (error) {
        return null;
      } finally {
        cohortDetailsStore.setIsLoadingCohort(false);
      }
    },
    [
      buildSearchParams,
      startDate,
      endDate,
      eg_nid,
      type,
      cohortDetailsStore,
      existingMemberUniqueIds,
    ],
  );

  const handleCohortChange = useCallback(
    async (index: number, value: any, type: CohortChangeType) => {
      let hasMultipleProperties = false;

      const updated = cohortDetailsList.map((cohort, i) => {
        if (i !== index) {
          return { ...cohort };
        }
        const next = { ...cohort, [type]: value };

        if (type === 'category') {
          next.dropdownSelectionTypeIndex = categoryDropDownOptions.indexOf(
            value,
          );
          next.multipleProperties = [];
          next.property = '';
        }
        if (type === 'property') {
          setPropertiesSearchValue(value);
        }
        if (type === 'multipleProperties') {
          hasMultipleProperties = Array.isArray(value) && value.length > 0;
        }
        return next;
      });

      const current = updated[index];
      const isComplete =
        current.category &&
        current.condition &&
        (current.property || hasMultipleProperties);

      if (isComplete) {
        const results = await getTotalMembers(updated, toggleList);
        const count = results ?? 0;
        const pct = roundNumber((count / totalResults) * 100, 2);
        updated[updated.length - 1].results = String(count);
        updated[updated.length - 1].percentage = String(pct);

        const totalCount = totalResults;
        const updatedFlowData = buildFilterFlowData(
          updated,
          cohortSelectionsTypes,
          totalCount,
        );
        cohortDetailsStore.setDraftFilterFlowData(updatedFlowData);
      }

      cohortDetailsStore.setDraftCohortDetailsList(updated);
    },
    [
      cohortDetailsList,
      categoryDropDownOptions,
      getTotalMembers,
      totalResults,
      cohortDetailsStore,
      cohortSelectionsTypes,
    ],
  );

  const handleScrollEvent = useCallback(
    async (
      category: string,
      index: number,
      dropdownSelectionTypeIndex: number,
      propertiesPageNumber: number,
      totalPagesForProperties: number,
    ) => {
      if (propertiesPageNumber + 1 < totalPagesForProperties) {
        handlePageNumber(
          index,
          propertiesPageNumber + 1,
          'propertiesPageNumber',
        );
        getPropertyValues(
          category,
          propertiesSearchValue,
          propertiesPageNumber + 1,
          false,
          index,
          dropdownSelectionTypeIndex,
        );
      }
    },
    [handlePageNumber, getPropertyValues, propertiesSearchValue],
  );

  const handleTextFieldForProperties = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement>,
      category: string,
      index: number,
      dropdownSelectionTypeIndex: number,
    ) => {
      handlePageNumber(index, 0, 'propertiesPageNumber');
      setPropertiesSearchValue(event.target.value);
      getPropertyValues(
        category,
        event.target.value,
        0,
        true,
        index,
        dropdownSelectionTypeIndex,
      );
    },
    [handlePageNumber, getPropertyValues],
  );

  const handleToggleChange = useCallback(
    (index: number | undefined, toggleValue?: 'left' | 'right') => {
      if (index === undefined) {
        return;
      }

      const newValue: 'and' | 'or' = toggleValue === 'left' ? 'and' : 'or';

      const updatedCohortList = cohortDetailsList.map((cohort, i) =>
        i === index
          ? {
              ...cohort,
              andOrCondition: newValue.toUpperCase() as 'AND' | 'OR',
            }
          : cohort,
      );
      cohortDetailsStore.setDraftCohortDetailsList(updatedCohortList);

      const updatedToggleList: ToggleValue[] = toggleList.map((item, i) =>
        i === index ? { toggleValue: newValue } : item,
      );
      updateToggleList(updatedToggleList);

      // ✅ Fix 2 — pass updatedCohortList to getTotalMembers so filterFlow rebuilds
      getTotalMembers(updatedCohortList, updatedToggleList).then((results) => {
        if (results === null) {
          return;
        }
        const count = results ?? 0;
        const pct = roundNumber(
          (count / parseInt(totalResults.toString().replaceAll(',', ''))) * 100,
          2,
        );

         
        const finalList = updatedCohortList.map((cohort, i) =>
          i === updatedCohortList.length - 1
            ? { ...cohort, results: String(count), percentage: String(pct) }
            : cohort,
        );

        cohortDetailsStore.setDraftCohortDetailsList(finalList);

        const updatedFlowData = buildFilterFlowData(
          finalList,
          cohortSelectionsTypes,
          totalResults,
        );
        cohortDetailsStore.setDraftFilterFlowData(updatedFlowData);
      });
    },
    [
      toggleList,
      cohortDetailsList,
      updateToggleList,
      getTotalMembers,
      cohortDetailsStore,
      cohortSelectionsTypes,
      totalResults,
    ],
  );

  const handleToggleList = useCallback(
    (index: number) => {
      let updatedToggleList = toggleList;

      if (cohortDetailsList.length >= 2) {
        updatedToggleList =
          index === 0
            ? toggleList.slice(1)
            : toggleList.filter((_, i) => i !== index - 1);
        updateToggleList(updatedToggleList);
      }

      getTotalMembers(cohortDetailsList, updatedToggleList);
    },
    [toggleList, cohortDetailsList, updateToggleList, getTotalMembers],
  );

  useEffect(() => {
    addToggleObject();
  }, [cohortDetailsList.length]);

  return {
    cohortDetailsList,
    toggleList,
    loadingPropertiesByIndex,
    handleToggleChange,
    handleToggleList,
    handleCohortChange,
    handleScrollEvent,
    handleTextFieldForProperties,
    getPropertyValues,
    getTotalMembers,
  };
};
